import React, { useState, useEffect } from 'react';
import { Home, User, Code, Briefcase, Mail, Sun, Moon, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

interface NavbarProps {
  activeSection: string;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  language: string;
  darkMode?: boolean;
  toggleDarkMode?: () => void;
  toggleLanguage?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ activeSection, mobileMenuOpen, setMobileMenuOpen, language, darkMode, toggleDarkMode, toggleLanguage }) => {
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      let text = '';
      if (hour >= 0 && hour < 11) {
        text = language === 'vi' ? 'Chào buổi sáng!' : 'Good morning!';
      } else if (hour >= 11 && hour < 14) {
        text = language === 'vi' ? 'Chào buổi trưa!' : 'Good noon!';
      } else if (hour >= 14 && hour < 18) {
        text = language === 'vi' ? 'Chào buổi chiều!' : 'Good afternoon!';
      } else {
        text = language === 'vi' ? 'Chào buổi tối!' : 'Good evening!';
      }
      setGreeting(text);
    };

    updateGreeting();
    const intervalId = setInterval(updateGreeting, 60000);
    return () => clearInterval(intervalId);
  }, [language]);

  const translations = {
    en: {
      home: 'Home',
      about: 'About',
      skills: 'Skills',
      projects: 'Projects',
      contact: 'Contact'
    },
    vi: {
      home: 'Trang Chủ',
      about: 'Giới Thiệu',
      skills: 'Kỹ Năng',
      projects: 'Dự Án',
      contact: 'Liên Hệ'
    }
  };

  const t = translations[language as keyof typeof translations];

  const navItems = [
    { id: 'home', label: t.home, icon: <Home size={20} />, path: '/Home' },
    { id: 'about', label: t.about, icon: <User size={20} />, path: '/About' },
    { id: 'skills', label: t.skills, icon: <Code size={20} />, path: '/Skills' },
    { id: 'projects', label: t.projects, icon: <Briefcase size={20} />, path: '/Projects' },
    { id: 'contact', label: t.contact, icon: <Mail size={20} />, path: '/Contact' },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Desktop Navigation - Centered */}
      <nav className="fixed top-8 left-1/2 transform -translate-x-1/2 z-40 hidden md:flex transition-all duration-500 ease-out">
        <ul className="flex space-x-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-8 py-3 rounded-full shadow-lg transition-all duration-500 hover:shadow-2xl hover:scale-105">
          {navItems.map((item, index) => (
            <li
              key={item.id}
              style={{
                animation: `slideDown 0.5s ease-out ${index * 0.1}s both`
              }}
            >
              <Link
                to={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                  window.history.pushState({}, '', item.path);
                }}
                className={`flex items-center px-4 py-2 rounded-full transition-all duration-500 group relative whitespace-nowrap overflow-hidden
                          ${activeSection === item.id
                            ? 'text-blue-600 dark:text-blue-300 scale-110'
                            : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-300 hover:scale-105'}`}
              >
                {/* Background pill effect */}
                <span className={`absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-600/10 dark:from-blue-400/10 dark:to-blue-500/10
                                 rounded-full transform transition-all duration-500 ease-out
                                 ${activeSection === item.id
                                   ? 'scale-100 opacity-100'
                                   : 'scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100'}`}></span>

                <span className="mr-2 transition-all duration-500 relative z-10">
                  {item.icon}
                </span>
                <span className="transition-all duration-500 group-hover:translate-x-1 relative z-10">
                  {item.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-30 md:hidden animate-in fade-in duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Navigation - Slide in from right */}
      <nav className={`fixed right-0 top-0 h-full w-64 bg-white/95 dark:bg-black/95 backdrop-blur-sm shadow-lg transition-all duration-500 ease-out z-40 md:hidden rounded-l-3xl
                      ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full p-6">
          <div className={`mb-10 mt-6 text-center transition-all duration-700 delay-100 ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Tai Nguyen</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 font-medium animate-in fade-in duration-500">{greeting}</p>
          </div>

          <ul className="space-y-4 flex-grow">
            {navItems.map((item, index) => (
              <li
                key={item.id}
                className={`transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                style={{
                  transitionDelay: mobileMenuOpen ? `${(index + 2) * 100}ms` : '0ms'
                }}
              >
                <Link
                  to={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.id);
                    window.history.pushState({}, '', item.path);
                  }}
                  className={`w-full flex items-center px-4 py-3 rounded-full transition-all duration-500 group relative overflow-hidden
                            ${activeSection === item.id
                              ? 'bg-blue-100/80 dark:bg-blue-900/80 backdrop-blur-sm text-blue-600 dark:text-blue-300 shadow-md scale-105'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 hover:scale-105'}`}
                >
                  {/* Background slide effect */}
                  <span className={`absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-600/20 dark:from-blue-400/20 dark:to-blue-500/20
                                   rounded-full transform transition-all duration-500 ease-out
                                   ${activeSection === item.id
                                     ? 'translate-x-0'
                                     : '-translate-x-full group-hover:translate-x-0'}`}></span>

                  <span className="mr-3 transition-all duration-500 relative z-10">
                    {item.icon}
                  </span>
                  <span className="text-lg transition-all duration-500 group-hover:translate-x-1 relative z-10">
                    {item.label}
                  </span>

                </Link>
              </li>
            ))}

            {/* Divider with Text */}
            <li className={`my-4 flex items-center justify-center gap-3 transition-all duration-500 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
                style={{ transitionDelay: mobileMenuOpen ? `${(navItems.length + 2) * 100}ms` : '0ms' }}>
              <div className="h-px bg-gray-200 dark:bg-gray-800 flex-1"></div>
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                {language === 'vi' ? 'Cài đặt' : 'Settings'}
              </span>
              <div className="h-px bg-gray-200 dark:bg-gray-800 flex-1"></div>
            </li>

            {/* Dark Mode Toggle */}
            <li className={`transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                style={{ transitionDelay: mobileMenuOpen ? `${(navItems.length + 3) * 100}ms` : '0ms' }}>
              <button
                onClick={toggleDarkMode}
                className="w-full flex items-center px-4 py-3 rounded-full transition-all duration-500 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 group"
              >
                <span className="mr-3 relative z-10 text-gray-600 dark:text-gray-400 group-hover:text-blue-500 transition-colors duration-300">
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </span>
                <span className="text-lg relative z-10 transition-transform duration-500 group-hover:translate-x-1">
                  {darkMode ? (language === 'vi' ? 'Chế độ sáng' : 'Light Mode') : (language === 'vi' ? 'Chế độ tối' : 'Dark Mode')}
                </span>
              </button>
            </li>

            {/* Language Switcher */}
            <li className={`transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                style={{ transitionDelay: mobileMenuOpen ? `${(navItems.length + 4) * 100}ms` : '0ms' }}>
              <button
                onClick={toggleLanguage}
                className="w-full flex items-center px-4 py-3 rounded-full transition-all duration-500 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 group"
              >
                <span className="mr-3 relative z-10 text-gray-600 dark:text-gray-400 group-hover:text-blue-500 transition-colors duration-300">
                  <Globe size={20} />
                </span>
                <span className="text-lg relative z-10 transition-transform duration-500 group-hover:translate-x-1">
                  {language === 'vi' ? 'English' : 'Tiếng Việt'}
                </span>
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
};

export default React.memo(Navbar);